import type { StateCreator } from "zustand";

import type {
  ZPipelineSettingsAction,
  ZPipelineSettingsState,
} from "../types/zustand";

export const pipelineSettingsActionSlice: StateCreator<
  ZPipelineSettingsAction & Partial<ZPipelineSettingsState>
> = (set, get) => ({
  deletePipelineById: pipelineId => {
    const pipelines = get().pipelines;
    const updatedPipelines = pipelines?.filter(
      pipeline => pipeline.id !== pipelineId,
    );
    set({ pipelines: updatedPipelines, activePipelineId: null });
  },

  updatePipelineById: (pipelineId, pipelineData) => {
    const pipelines = get().pipelines;
    const updatedPipelines = pipelines?.map(pipeline =>
      pipeline.id === pipelineId ? { ...pipeline, ...pipelineData } : pipeline,
    );
    set({ pipelines: updatedPipelines });
  },

  getActivePipeline: () => {
    const activePipelineId = get().activePipelineId;
    const pipelines = get().pipelines;
    return (
      pipelines?.find(pipeline => pipeline.id === activePipelineId) || null
    );
  },

  // Reorder the stages array when drag-and-drop ends
  reorderStages: (sourceIndex, destinationIndex) => {
    const activePipeline = get().getActivePipeline();
    if (!activePipeline) return [];

    const updatedStages = [...activePipeline.stages];
    const [removed] = updatedStages.splice(sourceIndex, 1);
    updatedStages.splice(destinationIndex, 0, removed);

    get().updatePipelineById(activePipeline.id, { stages: updatedStages });

    return updatedStages;
  },

  updateStage: (id, newStage) => {
    const activePipeline = get().getActivePipeline();
    if (!activePipeline) return;

    const updatedStages = activePipeline.stages.map(stage =>
      stage.stage_id === id ? { ...stage, ...newStage } : stage,
    );

    get().updatePipelineById(activePipeline.id, { stages: updatedStages });
  },

  // Add a new stage (just a simple demo)
  addStage: newStage => {
    const activePipeline = get().getActivePipeline();
    if (!activePipeline) return;

    // Check if the stage is added to the first position
    if (newStage.stage_order === 1) {
      get().updatePipelineById(activePipeline.id, {
        stages: [newStage, ...activePipeline.stages],
      });
    } else {
      get().updatePipelineById(activePipeline.id, {
        stages: [...activePipeline.stages, newStage],
      });
    }
  },

  removeStageByIds: ids => {
    const activePipeline = get().getActivePipeline();
    if (!activePipeline) return;

    const updatedStages = activePipeline.stages.filter(
      s => !ids.includes(s.stage_id),
    );
    get().updatePipelineById(activePipeline.id, { stages: updatedStages });
  },

  bulkUpdateStage: stageData => {
    const activePipeline = get().getActivePipeline();
    if (!activePipeline) return;

    const updatedStages = activePipeline.stages.map(stage =>
      stageData.stage_ids.includes(stage.stage_id)
        ? {
            ...stage,
            color: stageData.stage_color || stage.color,
            stage_type: stageData.stage_type || stage.stage_type,
          }
        : stage,
    );

    get().updatePipelineById(activePipeline.id, { stages: updatedStages });
  },
});
